import Cookies from "js-cookie";
import { RealtimeEventHandler } from "./event_handler.js";
import { RealtimeUtils } from "./utils.js";

export class RealtimeAPI extends RealtimeEventHandler {
  /**
   * Create a new RealtimeAPI instance
   * @param {{debug?: boolean,user?:string,belief_system?:str,voice?:str,topic_id?:str}} [settings]
   * @returns {RealtimeAPI}
   */
  constructor({ debug, user, voice, user_ids, belief_system, activation, opusDecoder, topic_id, resume_conversation_id } = {}) {
    super();
    this.belief_system = belief_system;
    this.voice = voice;
    this.debug = !!debug;
    this.user = user;
    this.user_ids = user_ids;
    this.activation = activation;
    this.topic_id = topic_id;
    this.resume_conversation_id = resume_conversation_id;
    this.ws = null;
    this.useBinaryAudio = true; // Enable binary audio transmission by default
    this.opusDecoder = opusDecoder || null;
    // Tracks the current assistant item id for grouping incoming binary audio
    this.currentBinaryItemId = null;
    // Buffer for binary audio chunks received before assistant item id is known
    this._pendingBinaryAudio = [];
    // Track last binary chunk time to detect when streaming is truly complete
    this._lastBinaryChunkTime = {};
    this._binaryCompletionTimers = {};

    // Maintain a rolling pointer to the active assistant message so we can tag binary audio
    // These listeners rely on .receive() dispatching server.* events
    this.on("server.conversation_item_created", (evt) => {
      try {
        const item = evt?.content?.item;
        if (item && item.type === "message" && item.role === "assistant") {
          // Mark current assistant message as the target for subsequent binary audio chunks
          this.currentBinaryItemId = item.id;
          // Flush any pending binary chunks to this newly created assistant item
          if (this._pendingBinaryAudio.length > 0) {
            for (const chunk of this._pendingBinaryAudio) {
              const synthetic = {
                type: "audio",
                delta: { audio: chunk },
                metadata: item.id,
              };
              this.receive("audio", synthetic);
            }
            this._pendingBinaryAudio = [];
          }
        }
      } catch (e) {
        if (this.debug) this.log("server.conversation_item_created handler error", String(e));
      }
    });
    this.on("server.output_item_done", (evt) => {
      try {
        const item = evt?.content?.item || evt?.metadata?.item;
        // Gate on the item being an assistant audio message rather than on
        // currentBinaryItemId. A single response can finish MULTIPLE assistant
        // items (e.g. a commentary item before a tool call, then the final
        // answer after it). Gating on currentBinaryItemId meant the second
        // item's done event was dropped once the first item's timer had nulled
        // the pointer, so its audio never got marked complete and playback
        // stalled forever ("Aris is talking" stuck). Each assistant message
        // item now gets its own independent completion timer.
        const isAssistantMessage =
          item && item.id &&
          (item.type ? item.type === "message" : true) &&
          (item.role ? item.role === "assistant" : true);
        if (isAssistantMessage) {
          // Don't immediately clear - binary chunks may still be arriving.
          // Instead, start an inactivity timer that will be reset on every
          // subsequent binary audio chunk. This ensures we only mark the
          // item complete after the *last* binary frame has arrived.
          const itemId = item.id;

          // Clear any existing timer for this item
          if (this._binaryCompletionTimers[itemId]) {
            clearTimeout(this._binaryCompletionTimers[itemId]);
          }

          // Wait for 3 seconds of inactivity before marking truly complete
          this._binaryCompletionTimers[itemId] = setTimeout(() => {
            this.receive("binary_audio_complete", { item_id: itemId });
            // Only clear the active pointer if it still refers to THIS item;
            // otherwise we'd wipe a newer item that is already streaming.
            if (this.currentBinaryItemId === itemId) {
              this.currentBinaryItemId = null;
            }
            delete this._lastBinaryChunkTime[itemId];
            delete this._binaryCompletionTimers[itemId];
          }, 3000); // 3 second grace period for final chunks
        }
      } catch (e) {
        if (this.debug) this.log("server.output_item_done handler error", String(e));
      }
    });
  }

  setOpusDecoder(decoder) {
    this.opusDecoder = decoder || null;
    return true;
  }

  /**
   * Set the topic_id for quick coaching topics
   * Call this before connect() to include topic_id in the WebSocket URL
   * @param {string} topicId - The topic ID to set
   */
  setTopicId(topicId) {
    this.topic_id = topicId;
  }

  /**
   * Set the option_id for quick coaching topics
   * Call this before connect() to include option_id in the WebSocket URL
   * @param {string} optionId - The option ID to set
   */
  setOptionId(optionId) {
    this.option_id = optionId;
  }

  /**
   * Set the dialog_type for specialized sessions (e.g. elements_assessment)
   * Call this before connect() to include dialog_type in the WebSocket URL
   * @param {string} dialogType - The dialog type to set
   */
  setDialogType(dialogType) {
    this.dialog_type = dialogType;
  }

  /**
   * Set the resume_conversation_id to resume a previous individual voice
   * conversation. Call this before connect() to include resume_conversation_id
   * in the WebSocket URL. Aris opens with a "welcome back" greeting and carries
   * the prior summary context. Resume is individual-coaching only — it is
   * ignored by the backend (and not sent here) when combined with couple/group
   * (user_ids), quick topics (topic_id/option_id) or element coaching.
   * @param {string} resumeConversationId - The conversation_id to resume
   */
  setResumeConversationId(resumeConversationId) {
    this.resume_conversation_id = resumeConversationId;
  }

  /**
   * Set element coaching / roleplay context.
   * Call before connect() to include the element-scoped query params per the
   * Element Coaching & Roleplay frontend contract.
   * @param {{topic?: string, domain?: string, level?: string, situation?: string, reason?: string, relationship?: string, scope?: string}} ctx
   */
  setElementContext(ctx = {}) {
    this.element_context = ctx || null;
  }

  /**
   * Tells us whether or not the WebSocket is connected
   * @returns {boolean}
   */
  isConnected() {
    return !!this.ws;
  }

  /**
   * Writes WebSocket logs to console
   * @param  {...any} args
   * @returns {true}
   */
  log(...args) {
    const date = new Date().toISOString();
    const logs = [`[Websocket/${date}]`].concat(args).map((arg) => {
      if (typeof arg === "object" && arg !== null) {
        return JSON.stringify(arg, null, 2);
      } else {
        return arg;
      }
    });
    if (this.debug) {
      console.error(...logs);
    }
    return true;
  }

  /**
   * Connects to Realtime API Websocket Server
   * @param {{model?: string}} [settings]
   * @returns {Promise<true>}
   */
  async connect({ model } = { model: "gpt-4o-realtime-preview-2024-12-17" }) {
    if (this.isConnected()) {
      throw new Error(`Already connected`);
    }

    if (!globalThis.WebSocket) {
      throw new Error("WebSocket is not supported in this environment");
    }

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const token = Cookies.get("idtoken");
    const queryParams = new URLSearchParams({
      belief_system: this.belief_system,
      voice: this.voice,
      activation: this.activation || false,
      timezone: userTimeZone,
      token: token,
      supports_binary_audio: true,
      supports_compressed_audio: true,
      preferred_audio_codec: "opus"
    });

    if (this.user_ids !== undefined) {
      queryParams.set('user_ids', this.user_ids);
    }

    // Add topic_id and option_id for quick coaching topics
    if (this.topic_id) {
      queryParams.set('topic_id', this.topic_id);
    }
    if (this.option_id) {
      queryParams.set('option_id', this.option_id);
    }
    // Add dialog_type for specialized sessions (e.g. elements_assessment)
    if (this.dialog_type) {
      queryParams.set('dialog_type', this.dialog_type);
    }

    // Resume a previous individual voice conversation. Only valid for individual
    // sessions — the backend ignores resume_conversation_id when combined with
    // user_ids (couple/group), topic_id/option_id (quick topics) or element
    // coaching, so we only send it for a plain individual session.
    if (
      this.resume_conversation_id &&
      this.user_ids === undefined &&
      !this.topic_id &&
      !this.option_id &&
      !this.element_context
    ) {
      queryParams.set('resume_conversation_id', this.resume_conversation_id);
    }

    // Element coaching / roleplay context (Element Coaching & Roleplay contract).
    if (this.element_context && typeof this.element_context === 'object') {
      const ec = this.element_context;
      if (ec.topic) queryParams.set('topic', ec.topic);
      if (ec.domain) queryParams.set('domain', ec.domain);
      if (ec.level) queryParams.set('level', ec.level);
      if (ec.situation) queryParams.set('situation', ec.situation);
      if (ec.reason) queryParams.set('reason', ec.reason);
      if (ec.relationship) queryParams.set('relationship', ec.relationship);
      if (ec.scope) queryParams.set('scope', ec.scope);
      // New contract fields (May 2026): prompt_variant for scope=element,
      // conversation_topic for scope=domain/level, question for the
      // literal first-turn user message.
      if (ec.prompt_variant) queryParams.set('prompt_variant', ec.prompt_variant);
      if (ec.conversation_topic) queryParams.set('conversation_topic', ec.conversation_topic);
      if (ec.question) queryParams.set('question', ec.question);
    }

    const url = this.activation ? `${process.env.REACT_APP_WS_HOST}/ARIS/voice/activation?${queryParams.toString()}` : `${process.env.REACT_APP_WS_URL}?${queryParams.toString()}`;
    const ws = new WebSocket(url);

    ws.addEventListener("message", async (event) => {
      const data = event.data;
      // If server sends binary audio frames (Blob/ArrayBuffer), convert to synthetic audio events
      if (data instanceof Blob || data instanceof ArrayBuffer) {
        try {
          await this.receiveBinaryAudio(data);
        } catch (e) {
          // If binary handling fails, do not crash the socket
          this.log("binary message handling error", String(e));
        }
        return;
      }
      // JSON message - parse normally
      const message = JSON.parse(data);
      this.receive(message.type, message);
    });

    return new Promise((resolve, reject) => {
      const connectionErrorHandler = () => {
        this.disconnect(ws);
        reject(new Error(`Could not connect to ${url}`));
      };

      ws.addEventListener("error", connectionErrorHandler);

      ws.addEventListener("open", () => {
        this.log(`Connected to ${url}`);
        ws.removeEventListener("error", connectionErrorHandler);

        ws.addEventListener("error", () => {
          this.disconnect(ws);
          this.log(`Error, disconnected from ${url}`);
          this.dispatch("close", { error: true });
        });

        ws.addEventListener("close", () => {
          this.disconnect(ws);
          this.log(`Disconnected from ${url}`);
          this.dispatch("close", { error: false });
        });

        this.ws = ws;
        resolve(true);
      });
    });
  }

  /**
   * Disconnects from Realtime API server
   * @param {WebSocket} [ws]
   * @returns {true}
   */
  disconnect(ws) {
    if (!ws || this.ws === ws) {
      this.ws && this.ws.close();
      this.ws = null;
      // Reset binary audio state so reconnects don't carry stale item IDs
      this.currentBinaryItemId = null;
      this._pendingBinaryAudio = [];
      // Clear any pending binary completion timers
      for (const timerId of Object.values(this._binaryCompletionTimers || {})) {
        clearTimeout(timerId);
      }
      this._binaryCompletionTimers = {};
      this._lastBinaryChunkTime = {};
      return true;
    }
  }

  /**
   * Receives an event from WebSocket and dispatches as "server.{eventName}" and "server.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  receive(eventName, event) {
    // this.log(`received:`, eventName, event);
    this.dispatch(`server.${eventName}`, event);
    this.dispatch("server.*", event);
    return true;
  }

  /**
   * Sends an event to WebSocket and dispatches as "client.{eventName}" and "client.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  send(eventName, data) {
    if (!this.isConnected()) {
      throw new Error(`RealtimeAPI is not connected`);
    }
    data = data || {};
    if (typeof data !== "object") {
      throw new Error(`data must be an object`);
    }
    const event = {
      event_id: RealtimeUtils.generateId("evt_"),
      type: eventName,
      ...data,
    };
    this.dispatch(`client.${eventName}`, event);
    this.dispatch("client.*", event);
    this.log(`sent:`, eventName, event);
    this.ws.send(JSON.stringify(event));
    return true;
  }

  /**
   * Receives a binary audio frame (Blob/ArrayBuffer) from server and dispatches a synthetic audio event
   * Server sends: [4 bytes header length][JSON metadata][raw PCM16 audio bytes]
   * The synthetic event matches the existing JSON shape expected by RealtimeConversation:
   *   { type: 'audio', delta: { audio: Int16Array }, metadata: item_id }
   * @param {Blob|ArrayBuffer} data
   * @returns {Promise<true>}
   */
  async receiveBinaryAudio(data) {
    // Convert Blob to ArrayBuffer when needed
    const arrayBuffer = data instanceof Blob ? await data.arrayBuffer() : data;
    
    // Parse binary frame format: [4 bytes header length][JSON metadata][raw PCM audio]
    const view = new DataView(arrayBuffer);
    
    // Read header length (4 bytes, little-endian uint32)
    const headerLength = view.getUint32(0, true);
    
    // Parse JSON header for timing and routing metadata
    let header = {};
    try {
      const headerBytes = new Uint8Array(arrayBuffer, 4, headerLength);
      const headerJson = new TextDecoder().decode(headerBytes);
      header = JSON.parse(headerJson);
    } catch (e) {
      if (this.debug) this.log("binary header parse error", String(e));
      header = {};
    }
    
    const codec = (header.codec || header.format || "pcm16").toString().toLowerCase();
    const headerSampleRate = Number(header.sample_rate) || 24000;
    const headerSampleCount = Number(header.sample_count);
    const headerDurationMs = Number(header.duration_ms);
    const headerItemId = header.item_id || header.itemId || null;

    const audioOffset = 4 + headerLength;
    const audioByteLength = arrayBuffer.byteLength - audioOffset;
    const payloadBuffer = arrayBuffer.slice(audioOffset, arrayBuffer.byteLength);

    let audioInt16;
    if (codec === "pcm16") {
      audioInt16 = new Int16Array(payloadBuffer);
    } else if (codec === "opus") {
      if (!this.opusDecoder || typeof this.opusDecoder.decode !== "function") {
        if (this.debug) this.log("opus_decoder_missing", { codec, audio_bytes: audioByteLength, headerSampleRate });
        return true;
      }
      // CRITICAL: Use decoder's configured sample rate, NOT backend header
      // Backend may send incorrect sample rate (e.g., 24000) when actual Opus is 48000
      // The decoder knows the correct rate from the Opus packets themselves
      const decoderSampleRate = this.opusDecoder.sampleRate || headerSampleRate;
      
      // Don't pass headerSampleCount for 48kHz - let decoder determine correct count
      const expectedCount = (decoderSampleRate === 48000) ? null : headerSampleCount;
      let decoded;
      try {
        decoded = await this.opusDecoder.decode(payloadBuffer, decoderSampleRate, expectedCount);
      } catch (err) {
        if (this.debug) this.log("opus_decode_error", String(err));
        return true;
      }
      if (decoded instanceof Int16Array) {
        audioInt16 = decoded;
      } else if (decoded instanceof ArrayBuffer) {
        audioInt16 = new Int16Array(decoded);
      } else if (ArrayBuffer.isView(decoded)) {
        const view = decoded;
        audioInt16 = new Int16Array(view.buffer, view.byteOffset, view.byteLength / 2);
      } else {
        if (this.debug) this.log("opus_decoder_invalid_output", { type: typeof decoded });
        return true;
      }
    } else {
      if (this.debug) this.log("unsupported_audio_codec", { codec, audio_bytes: audioByteLength });
      return true;
    }

    const computedSampleCount = audioInt16.length;
    // Use decoder's sample rate for accurate duration calculation
    const actualSampleRate = (codec === "opus" && this.opusDecoder) ? this.opusDecoder.sampleRate : headerSampleRate;
    const computedDurationMs = (computedSampleCount / actualSampleRate) * 1000;
    
    if (this.debug) {
      this.log("binary_audio_chunk", {
        header_type: header.type,
        header_item_id: headerItemId,
        current_item_id: this.currentBinaryItemId,
        raw_audio_bytes: audioByteLength,
        codec,
        header_sample_rate: headerSampleRate,
        actual_sample_rate: actualSampleRate,
        header_sample_count: headerSampleCount,
        computed_sample_count: computedSampleCount,
        header_duration_ms: headerDurationMs,
        computed_duration_ms: computedDurationMs,
      });
      
      // CRITICAL: Verify sample count - compare against expected count at actual sample rate
      if (codec === "opus" && headerSampleCount && computedSampleCount) {
        // If backend sent 24kHz metadata but we're using 48kHz, expect 2x samples
        const sampleRateRatio = actualSampleRate / headerSampleRate;
        const expectedSamplesAtActualRate = headerSampleCount * sampleRateRatio;
        const diff = Math.abs(computedSampleCount - expectedSamplesAtActualRate);
        const tolerance = expectedSamplesAtActualRate * 0.05;
        
        if (diff > tolerance) {
          console.warn(`⚠️ Sample count mismatch detected!`);
          console.warn(`   Backend header: ${headerSampleCount} samples @ ${headerSampleRate}Hz`);
          console.warn(`   Expected at ${actualSampleRate}Hz: ${expectedSamplesAtActualRate} samples`);
          console.warn(`   Decoder produced: ${computedSampleCount} samples`);
          console.warn(`   Difference: ${diff} samples (${(diff/expectedSamplesAtActualRate*100).toFixed(1)}%)`);
        } else {
          console.log(`✅ Sample count verified: ${computedSampleCount} samples @ ${actualSampleRate}Hz (backend: ${headerSampleCount} @ ${headerSampleRate}Hz)`);
        }
      }
    }
    
    // Determine which item this chunk belongs to: prefer item_id from header when present,
    // otherwise fall back to the currentBinaryItemId tracked from conversation events.
    const effectiveItemId = headerItemId || this.currentBinaryItemId;
    
    // If we don't yet know the assistant item id, queue the chunk for flushing
    if (!effectiveItemId) {
      this._pendingBinaryAudio.push(audioInt16);
      return true;
    }
    const itemId = effectiveItemId;
    
    // Track when this chunk arrived - used to detect completion
    this._lastBinaryChunkTime[itemId] = Date.now();
    
    // If there's a pending completion timer, reset it since we got new data
    if (this._binaryCompletionTimers[itemId]) {
      clearTimeout(this._binaryCompletionTimers[itemId]);
      // Restart the timer - wait 3 seconds after LAST chunk
      this._binaryCompletionTimers[itemId] = setTimeout(() => {
        this.receive("binary_audio_complete", { item_id: itemId });
        if (this.currentBinaryItemId === itemId) {
          this.currentBinaryItemId = null;
        }
        delete this._lastBinaryChunkTime[itemId];
        delete this._binaryCompletionTimers[itemId];
      }, 3000);
    }
    
    // Compose synthetic event and route through the standard receive/dispatch flow
    const synthetic = {
      type: "audio",
      delta: { audio: audioInt16 },
      metadata: itemId,
    };
    this.receive("audio", synthetic);
    return true;
  }

  /**
   * Sends binary audio data over WebSocket
   * Creates a binary frame with header containing event metadata and audio payload
   * Frame format: [4 bytes: header length][header JSON][audio PCM data]
   * @param {Int16Array|ArrayBuffer} audioData - Raw PCM audio data
   * @param {{[key: string]: any}} [metadata] - Optional metadata (e.g., wake-word flag, sample_rate)
   * @returns {true}
   */
  sendBinary(audioData, metadata = {}) {
    if (!this.isConnected()) {
      throw new Error(`RealtimeAPI is not connected`);
    }

    // Convert Int16Array to ArrayBuffer if needed
    const audioBuffer = audioData instanceof Int16Array ? audioData.buffer : audioData;
    
    // Create event header with metadata
    const event = {
      event_id: RealtimeUtils.generateId("evt_"),
      type: "input_audio_buffer.append",
      ...metadata,
    };
    
    const headerJson = JSON.stringify(event);
    const headerBytes = new TextEncoder().encode(headerJson);
    const headerLength = headerBytes.length;
    
    // Create binary frame: [4 bytes header length][header][audio data]
    const frame = new ArrayBuffer(4 + headerLength + audioBuffer.byteLength);
    const view = new DataView(frame);
    
    // Write header length (4 bytes, little-endian)
    view.setUint32(0, headerLength, true);
    
    // Write header JSON
    const frameBytes = new Uint8Array(frame);
    frameBytes.set(headerBytes, 4);
    
    // Write audio data
    frameBytes.set(new Uint8Array(audioBuffer), 4 + headerLength);
    
    // Dispatch events for logging
    this.dispatch(`client.input_audio_buffer.append`, event);
    this.dispatch("client.*", event);
    this.log(`sent binary:`, event.type, `audio bytes: ${audioBuffer.byteLength}, metadata:`, metadata);
    
    // Send binary frame
    this.ws.send(frame);
    return true;
  }
}